import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Skeleton } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import * as atoms from '../../../atoms';
import useAiChatPanel from '../../../hooks/useAiChatPanel';
import useAccountPermission from '../../../hooks/useAccountPermission';
import useSelectedEnv from '../../../hooks/useSelectedEnv';
import { Flex, FlexColumn, FlexSpace } from '../../Flex';
import { NAVBAR_WIDTH, COLLAPSED_NAVBAR_WIDTH } from '../../Navbar';
import { ResizablePanelHorizontal } from '../../ResizablePanel';
import {
  AI_AGENT,
  FALLBACK_CHAT_CONFIG,
  ROUTE_CHAT_CONFIGS,
  SIDE_PANEL_WIDTH,
  RouteChatConfig,
  getChatStarter,
} from '../constants';
import type { ChatStarterKey } from '../constants';
import { Announcement, ChatIntegration, ChatMode } from '../types';
import { AgentOrigin } from '../../../types';
import useAvailableModes from '../hooks/useAvailableModes';
import { fetchChatIntegrations } from '../../../services/api';
import { CHAT_INTEGRATIONS } from '../../../constants/queryKeys';
import { HistoryData, useChatHistory } from '../hooks/useChatHistory';
import { chatBackground } from '../styles';
import { space } from '../../../style/utils';
import AiChat from './AiChat';
import AiChatTitle from './AiChatTitle';
import ChatHistoryPanel from './ChatHistoryPanel';
import { ChatSettings } from './ChatSettings';

const MIN_PANEL_WIDTH = 300;
const MAX_PANEL_WIDTH = 800;
const CHAT_MAX_WIDTH = 768;

const Header = styled(FlexSpace)<{ $isFullWidth?: boolean }>`
  padding: ${({ $isFullWidth }) =>
    $isFullWidth ? space(0, 0, 0, 'xxl') : space('lg', 'xxl')};
  ${({ $isFullWidth }) => $isFullWidth && 'align-items: stretch;'}
  border-bottom: 1px solid ${({ theme }) => theme.palette.border.divider};
  flex-shrink: 0;
  height: 57px;
`;

const ContentRow = styled(Flex)<{ $column?: boolean }>`
  flex: 1;
  min-height: 0;
  overflow: hidden;
  position: relative;
  flex-direction: ${({ $column }) => ($column ? 'column' : 'row')};
`;

const ChatContainer = styled(FlexColumn)<{
  $visible?: boolean;
  $center?: boolean;
}>`
  flex: 1;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  display: ${({ $visible = true }) => ($visible ? 'flex' : 'none')};
  ${({ $center }) =>
    $center &&
    `
    align-items: center;
    > * { width: 100%; }
  `}
`;

const SkeletonContainer = styled(FlexColumn)<{ $maxWidth?: number }>`
  padding: ${space('xxl')};
  gap: ${space('xxl')};
  flex: 1;
  width: 100%;
  ${({ $maxWidth }) => $maxWidth && `max-width: ${$maxWidth}px;`}
  align-self: center;
`;

const MessageSkeleton = styled(FlexColumn)<{ $align?: 'left' | 'right' }>`
  gap: ${space('sm')};
  align-items: ${({ $align = 'left' }) =>
    $align === 'right' ? 'flex-end' : 'flex-start'};
  max-width: 70%;
  ${({ $align }) => $align === 'right' && 'align-self: flex-end;'}
`;

const ChatSkeleton = ({ maxWidth }: { maxWidth?: number }) => (
  <SkeletonContainer $maxWidth={maxWidth}>
    <MessageSkeleton $align="right">
      <Skeleton variant="rounded" width={220} height={40} />
    </MessageSkeleton>
    <MessageSkeleton>
      <Skeleton variant="rounded" width={280} height={20} />
      <Skeleton variant="rounded" width={320} height={20} />
      <Skeleton variant="rounded" width={180} height={20} />
    </MessageSkeleton>
    <MessageSkeleton $align="right">
      <Skeleton variant="rounded" width={180} height={40} />
    </MessageSkeleton>
    <MessageSkeleton>
      <Skeleton variant="rounded" width={300} height={20} />
      <Skeleton variant="rounded" width={240} height={20} />
    </MessageSkeleton>
  </SkeletonContainer>
);

const SidePanelContainer = styled(FlexColumn)`
  width: ${SIDE_PANEL_WIDTH}px;
  min-width: ${SIDE_PANEL_WIDTH}px;
  height: 100%;
  border-left: 1px solid ${({ theme }) => theme.palette.border.divider};
  overflow: hidden;
  flex-shrink: 0;
`;

const Wrapper = styled(FlexColumn)<{
  $width: number;
  $isExpanded: boolean;
  $isFullWidth: boolean;
  $isOverlay: boolean;
  $navbarWidth: number;
  $isDragging: boolean;
}>`
  height: 100%;
  width: ${({ $width, $isExpanded, $isFullWidth, $navbarWidth }) =>
    $isFullWidth
      ? `calc(100% - ${$navbarWidth}px)`
      : $isExpanded
      ? `${$width}px`
      : '0px'};
  min-width: ${({ $width, $isExpanded, $isFullWidth }) =>
    $isFullWidth ? 0 : $isExpanded ? `${$width}px` : '0px'};
  transition: ${({ $isDragging }) =>
    $isDragging ? 'none' : 'width 150ms ease, min-width 150ms ease'};
  border-left: ${({ theme, $isExpanded, $isOverlay }) =>
    $isExpanded && !$isOverlay
      ? `1px solid ${theme.palette.border.divider}`
      : 'none'};
  position: ${({ $isOverlay }) => ($isOverlay ? 'absolute' : 'relative')};
  top: ${({ $isOverlay }) => ($isOverlay ? 0 : 'auto')};
  right: ${({ $isOverlay }) => ($isOverlay ? 0 : 'auto')};
  bottom: ${({ $isOverlay }) => ($isOverlay ? 0 : 'auto')};
  z-index: ${({ theme }) => theme.zIndex.drawer + 3};

  overflow: hidden;
  ${chatBackground}
`;

const Placeholder = styled.div<{ $width: number }>`
  width: ${({ $width }) => $width}px;
  min-width: ${({ $width }) => $width}px;
  height: 100%;
  flex-shrink: 0;
`;

const TITLE = 'Elementary AI';

const getRouteChatConfig = (pathname: string): RouteChatConfig => {
  const match = ROUTE_CHAT_CONFIGS.find((entry) =>
    pathname.includes(entry.pattern),
  );
  return match?.config ?? FALLBACK_CHAT_CONFIG;
};

type AiChatPanelProps = {
  onBlockedAction?: (message: string) => void;
};

const AiChatPanel = ({ onBlockedAction }: AiChatPanelProps) => {
  const {
    isExpanded,
    isFullWidth,
    width,
    closePanel,
    setWidth,
    toggleFullWidth,
    initialConfig,
    clearInitialConfig,
    context,
    setContext,
  } = useAiChatPanel();
  const { isCollapsed: isNavbarCollapsed } = useRecoilValue(
    atoms.navbarCollapsed,
  );
  const navbarWidth = isNavbarCollapsed ? COLLAPSED_NAVBAR_WIDTH : NAVBAR_WIDTH;
  const availableModes = useAvailableModes();
  const fallbackMode: ChatMode =
    (availableModes.includes('agent') ? 'agent' : availableModes[0]) ?? 'agent';
  const [chatPanel, setChatPanel] = useRecoilState(atoms.aiChatPanel);
  const selectedMode: ChatMode =
    chatPanel.selectedMode && availableModes.includes(chatPanel.selectedMode)
      ? chatPanel.selectedMode
      : fallbackMode;
  const setSelectedMode = useCallback(
    (mode: ChatMode) =>
      setChatPanel((prev) => ({ ...prev, selectedMode: mode })),
    [setChatPanel],
  );
  const enabledIntegrations = chatPanel.enabledIntegrations;
  const setEnabledIntegrations = useCallback(
    (values: ChatIntegration[]) =>
      setChatPanel((prev) => ({ ...prev, enabledIntegrations: values })),
    [setChatPanel],
  );

  const [isOverlay, setIsOverlay] = useState(isFullWidth);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (isFullWidth) {
      setIsOverlay(true);
    }
  }, [isFullWidth]);

  const handleTransitionEnd = useCallback(() => {
    if (!isFullWidth) {
      setIsOverlay(false);
    }
  }, [isFullWidth]);

  const selectedEnvId = useSelectedEnv();
  const { pathname } = useLocation();
  const isDeepLink = pathname.startsWith('/ai-chat');
  const routeChatConfig = useMemo(
    () => getRouteChatConfig(pathname),
    [pathname],
  );

  const [chatComponentKey, setChatComponentKey] = useState(0);
  const [showSettings, setShowSettings] = useState(false);
  const [startMessage, setStartMessage] = useState<string | undefined>();
  const [starterKey, setStarterKey] = useState<ChatStarterKey | undefined>();
  const [openOrigin, setOpenOrigin] = useState<AgentOrigin | undefined>();
  const [isChatEmpty, setIsChatEmpty] = useState(true);
  const [activeThreadId, setActiveThreadId] = useState<string | undefined>();
  const [focusToolCallId, setFocusToolCallId] = useState<string | undefined>();
  const [isLoadingThread, setIsLoadingThread] = useState(false);
  const configRef = useRef(initialConfig);
  const prevEnvIdRef = useRef(selectedEnvId);

  const starter = useMemo(
    () => (starterKey ? getChatStarter(starterKey, context) : undefined),
    [starterKey, context],
  );

  const navigate = useNavigate();
  const hasAccountPermission = useAccountPermission();
  const canViewAiRules = hasAccountPermission('VIEW_AI_RULES');
  const settingsVisible = showSettings && canViewAiRules;
  const { data: chatIntegrations } = useQuery(
    [CHAT_INTEGRATIONS],
    fetchChatIntegrations,
  );
  // Full-width chat only: the sidepanel is too narrow for the banner (it wraps
  // and covers the empty state), so Connect collapses back to the sidepanel.
  const contextualAnnouncements: Announcement[] = useMemo(() => {
    if (
      !isFullWidth ||
      chatIntegrations === undefined ||
      !hasAccountPermission('EDIT_INTEGRATIONS')
    ) {
      return [];
    }

    // Webhook-only Fivetran accounts get the reconnect CTA instead of the dbt
    // Cloud one.
    if (chatIntegrations.fivetran_webhook_only) {
      return [
        {
          id: 'connect-fivetran-api',
          prefix: 'New',
          message: 'Reconnect Fivetran via API for the agent connector.',
          cta: {
            label: 'Connect',
            onClick: () => {
              setChatPanel((prev) => ({ ...prev, isFullWidth: false }));
              navigate('/settings/environments');
            },
          },
        },
      ];
    }

    if (!chatIntegrations.integrations.includes('dbt_cloud')) {
      return [
        {
          id: 'connect-dbt-cloud',
          prefix: 'New',
          message: 'Connect dbt Cloud to let the agent pull job & run data.',
          cta: {
            label: 'Connect',
            onClick: () => {
              setChatPanel((prev) => ({ ...prev, isFullWidth: false }));
              navigate('/settings/ai?tab=mcp');
            },
          },
        },
      ];
    }

    return [];
  }, [
    isFullWidth,
    chatIntegrations,
    hasAccountPermission,
    navigate,
    setChatPanel,
  ]);

  const { history, resetChatHistory, handleSelectHistory } = useChatHistory({
    open: isExpanded,
  });

  const resetChat = useCallback(() => {
    setChatComponentKey((prevKey) => prevKey + 1);
    resetChatHistory();
    setStartMessage(undefined);
    setStarterKey(undefined);
  }, [resetChatHistory]);

  useEffect(() => {
    if (prevEnvIdRef.current === selectedEnvId) return;
    prevEnvIdRef.current = selectedEnvId;
    const hasExplicitEnvScope = (context?.environmentIds?.length ?? 0) > 0;
    if (activeThreadId || hasExplicitEnvScope) return;
    resetChat();
  }, [selectedEnvId, resetChat, activeThreadId, context]);

  useEffect(() => {
    if (!initialConfig || initialConfig === configRef.current) return;
    configRef.current = initialConfig;

    if (initialConfig.initialThreadId) {
      const threadId = initialConfig.initialThreadId;
      setFocusToolCallId(initialConfig.focusToolCallId);
      clearInitialConfig();
      if (threadId === activeThreadId) {
        return;
      }
      handleSelectHistory({ threadId, messages: [] });
      return;
    }

    setFocusToolCallId(undefined);
    setStartMessage(initialConfig.startMessage);
    setStarterKey(initialConfig.starterKey);
    setOpenOrigin(initialConfig.origin);
    setChatComponentKey((prevKey) => prevKey + 1);
    resetChatHistory();
    setShowSettings(false);
    clearInitialConfig();
  }, [
    initialConfig,
    clearInitialConfig,
    resetChatHistory,
    selectedEnvId,
    handleSelectHistory,
    activeThreadId,
  ]);

  const handleSelectHistoryClick = (historyData: HistoryData) => {
    setIsLoadingThread(false);
    handleSelectHistory(historyData);
    setStarterKey(undefined);
    if (!isFullWidth) {
      setShowSettings(false);
    }
  };

  const buildConversationLink = useCallback(
    (threadId: string) =>
      `${window.location.origin}/ai-chat/${threadId}${
        selectedEnvId ? `?env=${selectedEnvId}` : ''
      }`,
    [selectedEnvId],
  );

  const handleCopyLink = useCallback(() => {
    if (!activeThreadId || isChatEmpty) return;
    navigator.clipboard.writeText(buildConversationLink(activeThreadId));
  }, [activeThreadId, isChatEmpty, buildConversationLink]);

  useEffect(() => {
    if (!pathname.startsWith('/ai-chat')) return;
    const target =
      activeThreadId && !isChatEmpty
        ? buildConversationLink(activeThreadId)
        : `${window.location.origin}/ai-chat`;
    if (window.location.href !== target) {
      window.history.replaceState(null, '', target);
    }
  }, [activeThreadId, isChatEmpty, pathname, buildConversationLink]);

  const handleDrag = (newWidth: number | ((prev: number) => number)) => {
    const resolvedWidth =
      typeof newWidth === 'function' ? newWidth(width) : newWidth;
    setWidth(resolvedWidth);
  };

  return (
    <>
      {!isOverlay && (
        <ResizablePanelHorizontal
          width={width}
          minWidth={MIN_PANEL_WIDTH}
          maxWidth={MAX_PANEL_WIDTH}
          onDrag={handleDrag}
          onDragStart={() => setIsDragging(true)}
          onDragEnd={() => setIsDragging(false)}
          onMouseEnter={() => null}
          onMouseLeave={() => null}
          direction="right-to-left"
        />
      )}
      {isOverlay && <Placeholder $width={width} />}
      <Wrapper
        $width={width}
        $isExpanded={isExpanded}
        $isFullWidth={isFullWidth}
        $isOverlay={isOverlay}
        $navbarWidth={navbarWidth}
        $isDragging={isDragging}
        onTransitionEnd={handleTransitionEnd}
      >
        <Header $isFullWidth={isFullWidth}>
          <AiChatTitle
            title={TITLE}
            onNewChat={() => {
              resetChat();
              if (!isFullWidth) setShowSettings(false);
            }}
            onSelectHistory={handleSelectHistoryClick}
            onThreadLoadStart={() => setIsLoadingThread(true)}
            onOpenSettings={
              canViewAiRules
                ? () => setShowSettings((prev) => !prev)
                : undefined
            }
            onOpenHistory={
              isFullWidth ? () => setShowSettings(false) : undefined
            }
            onClose={closePanel}
            onToggleFullWidth={
              isFullWidth && isDeepLink ? undefined : toggleFullWidth
            }
            isFullWidth={isFullWidth}
            activeSidePanel={settingsVisible ? 'settings' : 'history'}
            activeThreadId={isChatEmpty ? undefined : activeThreadId}
            onCopyLink={handleCopyLink}
          />
        </Header>
        <ContentRow $column={!isFullWidth}>
          {!isFullWidth && canViewAiRules && (
            <ChatContainer $visible={settingsVisible}>
              <ChatSettings chatName={TITLE} />
            </ChatContainer>
          )}
          <ChatContainer
            $visible={isFullWidth || !settingsVisible}
            $center={isFullWidth}
          >
            {isLoadingThread ? (
              <ChatSkeleton
                maxWidth={isFullWidth ? CHAT_MAX_WIDTH : undefined}
              />
            ) : (
              <AiChat
                key={chatComponentKey}
                placeholder={AI_AGENT.placeholder}
                chatHistory={history}
                startMessage={startMessage}
                openOrigin={openOrigin}
                starter={starter}
                context={context}
                onContextChange={setContext}
                suggestions={routeChatConfig.suggestions}
                onEmptyStateChange={setIsChatEmpty}
                onThreadIdChange={setActiveThreadId}
                contentMaxWidth={isFullWidth ? CHAT_MAX_WIDTH : undefined}
                availableModes={availableModes}
                mode={selectedMode}
                onModeChange={setSelectedMode}
                enabledIntegrations={enabledIntegrations}
                onEnabledIntegrationsChange={setEnabledIntegrations}
                onBlockedAction={onBlockedAction}
                focusToolCallId={focusToolCallId}
                contextualAnnouncements={contextualAnnouncements}
              />
            )}
          </ChatContainer>
          {isFullWidth && (
            <SidePanelContainer>
              {settingsVisible ? (
                <ChatSettings chatName={TITLE} hideHeader />
              ) : (
                <ChatHistoryPanel
                  onSelectHistory={handleSelectHistoryClick}
                  onThreadLoadStart={() => setIsLoadingThread(true)}
                  activeThreadId={activeThreadId}
                />
              )}
            </SidePanelContainer>
          )}
        </ContentRow>
      </Wrapper>
    </>
  );
};

export default AiChatPanel;
